import React from "react"
import {renderToStaticMarkup} from "react-dom/server"
import {Award, CheckCircle2, Clock, ListChecks, LucideIcon, MessageSquareWarning, Target, TrendingUp} from "lucide-react"

export type ResultCardContent = {
    logoSrc: string
    testTitle: string
    dateLabel: string
    learnerName?: string
    /** Small pills under the title, e.g. exam, section, mode. */
    tags: string[]
    score: string
    /** Drives the progress ring; omitted when the exam has no fixed maximum. */
    scoreValue?: number
    scoreMax?: number
    scaleLabel?: string
    performance: string
    cefr?: string
    descriptor: string
    stats: { label: string, value: string, kind: "questions" | "time" }[]
    strengths: string[]
    improvements: string[]
    labels: {
        badge: string
        overallScore: string
        performance: string
        descriptor: string
        strengths: string
        improvements: string
        footer: string
    }
}

const WIDTH = 1080
const MIN_HEIGHT = 1350
const MARGIN = 64
const INNER = WIDTH - MARGIN * 2
const GAP = 24
const FONT = "Raleway, sans-serif"
const COLORS = {
    background: "#f6f7fb",
    card: "#ffffff",
    border: "#e7e9f0",
    text: "#111827",
    muted: "#6b7280",
    faint: "#9ca3af",
    primary: "#ff69d9",
    primarySoft: "#fff0fa",
    primaryTint: "#ffd9f4",
    primaryDeep: "#c2189b",
    secondary: "#3b82f6",
    secondarySoft: "#e8f0fe",
    neutral: "#6b7280",
    track: "#f3e6ef",
    success: "#16a34a",
    successSoft: "#ecfdf3",
    danger: "#dc2626",
    dangerSoft: "#fef2f2",
}

const loadImage = (src: string) => new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = reject
    image.src = src
})

/** The lucide icons the result screen uses, as images the canvas can draw. */
const iconImage = (Icon: LucideIcon, color: string, size = 44) =>
    loadImage(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(renderToStaticMarkup(<Icon color={color} size={size} strokeWidth={2}/>))}`)

/** Splits text into at most `maxLines` lines that fit `width`, ending with "…" when cut. */
const wrap = (context: CanvasRenderingContext2D, text: string, width: number, maxLines: number) => {
    const lines: string[] = []
    let line = ""
    for (const word of String(text || "").split(/\s+/).filter(Boolean)) {
        const candidate = line ? `${line} ${word}` : word
        if (context.measureText(candidate).width <= width || !line)
            line = candidate
        else {
            lines.push(line)
            line = word
        }
    }
    if (line) lines.push(line)
    if (lines.length > maxLines) {
        lines.length = maxLines
        let last = lines[maxLines - 1]
        while (last && context.measureText(`${last}…`).width > width) last = last.slice(0, -1)
        lines[maxLines - 1] = `${last.trimEnd()}…`
    }
    return lines
}

const roundedRect = (context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) => {
    context.beginPath()
    context.moveTo(x + radius, y)
    context.arcTo(x + width, y, x + width, y + height, radius)
    context.arcTo(x + width, y + height, x, y + height, radius)
    context.arcTo(x, y + height, x, y, radius)
    context.arcTo(x, y, x + width, y, radius)
    context.closePath()
}

const panel = (context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number,
               fill = COLORS.card, stroke: string | undefined = COLORS.border) => {
    roundedRect(context, x, y, width, height, 28)
    context.fillStyle = fill
    context.fill()
    if (stroke) {
        context.strokeStyle = stroke
        context.lineWidth = 2
        context.stroke()
    }
}

/** A rounded pill; returns its width so pills can be laid out in a row. */
const pill = (context: CanvasRenderingContext2D, text: string, x: number, y: number, fill: string, color: string,
              fontSize = 26, paddingX = 22, height = 50) => {
    context.font = `600 ${fontSize}px ${FONT}`
    const width = context.measureText(text).width + paddingX * 2
    roundedRect(context, x, y, width, height, height / 2)
    context.fillStyle = fill
    context.fill()
    context.fillStyle = color
    context.textBaseline = "middle"
    context.fillText(text, x + paddingX, y + height / 2 + 1)
    context.textBaseline = "top"
    return width
}

const label = (context: CanvasRenderingContext2D, icon: HTMLImageElement, text: string, color: string, x: number, y: number) => {
    context.drawImage(icon, x, y - 4, 40, 40)
    context.fillStyle = color
    context.font = `600 32px ${FONT}`
    context.fillText(text, x + 54, y)
}

/** Soft brand circles and a dot grid in the header — decoration without gradients. */
const decorateHeader = (context: CanvasRenderingContext2D, height: number) => {
    context.fillStyle = COLORS.primarySoft
    context.fillRect(0, 0, WIDTH, height)

    context.save()
    context.beginPath()
    context.rect(0, 0, WIDTH, height)
    context.clip()
    context.fillStyle = COLORS.primaryTint
    context.beginPath()
    context.arc(WIDTH - 40, 10, 190, 0, Math.PI * 2)
    context.fill()
    context.fillStyle = COLORS.secondarySoft
    context.beginPath()
    context.arc(WIDTH - 250, height + 20, 95, 0, Math.PI * 2)
    context.fill()
    context.restore()

    context.fillStyle = "#ffb8ec"
    for (let row = 0; row < 4; row++)
        for (let column = 0; column < 6; column++) {
            context.beginPath()
            context.arc(WIDTH - 430 + column * 26, 40 + row * 26, 4, 0, Math.PI * 2)
            context.fill()
        }
}

/** Score ring: the arc covers score / max of the exam's scale. */
const scoreRing = (context: CanvasRenderingContext2D, centerX: number, centerY: number, content: ResultCardContent) => {
    const radius = 118
    context.lineWidth = 24
    context.lineCap = "round"
    context.strokeStyle = COLORS.track
    context.beginPath()
    context.arc(centerX, centerY, radius, 0, Math.PI * 2)
    context.stroke()

    if (content.scoreMax && content.scoreValue !== undefined) {
        const share = Math.max(0, Math.min(1, content.scoreValue / content.scoreMax))
        if (share > 0) {
            context.strokeStyle = COLORS.primary
            context.beginPath()
            context.arc(centerX, centerY, radius, -Math.PI / 2, -Math.PI / 2 + share * Math.PI * 2)
            context.stroke()
        }
    }

    context.textAlign = "center"
    context.textBaseline = "middle"
    context.fillStyle = COLORS.text
    context.font = `700 ${content.score.length > 3 ? 72 : 96}px ${FONT}`
    context.fillText(content.score, centerX, centerY - (content.scoreMax ? 22 : 0))
    if (content.scoreMax) {
        context.fillStyle = COLORS.muted
        context.font = `600 30px ${FONT}`
        context.fillText(`/ ${content.scoreMax}`, centerX, centerY + 58)
    }
    context.textAlign = "left"
    context.textBaseline = "top"
}

/**
 * Draws the shareable result card — the result screen's overview with more detail and brand
 * decoration — and returns it as a PNG. The height grows with what there is to show.
 */
export const drawResultCard = async (content: ResultCardContent): Promise<Blob> => {
    await Promise.all([
        document.fonts?.load(`700 64px ${FONT}`),
        document.fonts?.load(`600 32px ${FONT}`),
        document.fonts?.load(`500 32px ${FONT}`),
    ]).catch(() => undefined)

    const [logo, scoreIcon, performanceIcon, descriptorIcon, strengthIcon, improveIcon, questionsIcon, timeIcon] = await Promise.all([
        loadImage(content.logoSrc),
        iconImage(Award, COLORS.primary),
        iconImage(TrendingUp, COLORS.secondary),
        iconImage(MessageSquareWarning, COLORS.neutral),
        iconImage(CheckCircle2, COLORS.success),
        iconImage(Target, COLORS.danger),
        iconImage(ListChecks, COLORS.primaryDeep),
        iconImage(Clock, COLORS.primaryDeep),
    ])

    // Draw on a tall scratch canvas, then crop to the content.
    const canvas = document.createElement("canvas")
    canvas.width = WIDTH
    canvas.height = 2400
    const context = canvas.getContext("2d")
    context.textBaseline = "top"
    context.fillStyle = COLORS.background
    context.fillRect(0, 0, WIDTH, canvas.height)

    // Header: logo and badge
    const headerHeight = 200
    decorateHeader(context, headerHeight)
    const logoHeight = 84
    context.drawImage(logo, MARGIN, 58, logo.width * (logoHeight / logo.height), logoHeight)
    context.font = `700 24px ${FONT}`
    const badgeWidth = context.measureText(content.labels.badge).width + 44
    pill(context, content.labels.badge, WIDTH - MARGIN - badgeWidth, 76, COLORS.card, COLORS.primaryDeep, 24, 22, 48)

    // Title, tags, learner and date
    let y = headerHeight + 44
    context.fillStyle = COLORS.text
    context.font = `700 54px ${FONT}`
    for (const line of wrap(context, content.testTitle, INNER, 2)) {
        context.fillText(line, MARGIN, y)
        y += 64
    }
    y += 14
    let tagX = MARGIN
    content.tags.filter(Boolean).forEach((tag, index) => {
        const colors = [[COLORS.primarySoft, COLORS.primaryDeep], [COLORS.secondarySoft, COLORS.secondary], ["#f1f2f6", COLORS.muted]][index % 3]
        tagX += pill(context, tag, tagX, y, colors[0], colors[1]) + 12
    })
    y += 50 + 22
    context.fillStyle = COLORS.muted
    context.font = `500 30px ${FONT}`
    context.fillText([content.learnerName, content.dateLabel].filter(Boolean).join("  ·  "), MARGIN, y)
    y += 62

    // Score and performance
    const heroHeight = 380
    panel(context, MARGIN, y, INNER, heroHeight)
    label(context, scoreIcon, content.labels.overallScore, COLORS.primary, MARGIN + 40, y + 36)
    scoreRing(context, MARGIN + 40 + 128, y + 96 + 136, content)

    const rightX = MARGIN + 360
    const rightWidth = MARGIN + INNER - 40 - rightX
    let rightY = y + 110
    label(context, performanceIcon, content.labels.performance, COLORS.secondary, rightX, rightY)
    rightY += 60
    context.fillStyle = COLORS.text
    context.font = `700 54px ${FONT}`
    for (const line of wrap(context, content.performance, rightWidth, 2)) {
        context.fillText(line, rightX, rightY)
        rightY += 64
    }
    rightY += 12
    let chipX = rightX
    if (content.cefr)
        chipX += pill(context, `CEFR ${content.cefr}`, chipX, rightY, COLORS.secondarySoft, COLORS.secondary) + 12
    if (content.scaleLabel)
        pill(context, content.scaleLabel, chipX, rightY, "#f1f2f6", COLORS.muted)
    y += heroHeight + GAP

    // Result descriptor
    context.font = `500 34px ${FONT}`
    const descriptorLines = wrap(context, content.descriptor, INNER - 80, 3)
    const descriptorHeight = 116 + descriptorLines.length * 48
    panel(context, MARGIN, y, INNER, descriptorHeight)
    label(context, descriptorIcon, content.labels.descriptor, COLORS.neutral, MARGIN + 40, y + 36)
    context.fillStyle = COLORS.text
    context.font = `500 34px ${FONT}`
    descriptorLines.forEach((line, index) => context.fillText(line, MARGIN + 40, y + 98 + index * 48))
    y += descriptorHeight + GAP

    // Stats tiles
    if (content.stats.length) {
        const tileWidth = (INNER - GAP * (content.stats.length - 1)) / content.stats.length
        content.stats.forEach((stat, index) => {
            const x = MARGIN + index * (tileWidth + GAP)
            panel(context, x, y, tileWidth, 128, COLORS.primarySoft, undefined)
            context.drawImage(stat.kind === "time" ? timeIcon : questionsIcon, x + 32, y + 42, 44, 44)
            context.fillStyle = COLORS.text
            context.font = `700 44px ${FONT}`
            context.fillText(stat.value, x + 96, y + 24)
            context.fillStyle = COLORS.muted
            context.font = `500 26px ${FONT}`
            context.fillText(wrap(context, stat.label, tileWidth - 120, 1)[0] || "", x + 96, y + 78)
        })
        y += 128 + GAP
    }

    // Strengths and areas to improve
    const feedbackColumns = [
        {items: content.strengths, title: content.labels.strengths, icon: strengthIcon, color: COLORS.success, fill: COLORS.successSoft},
        {items: content.improvements, title: content.labels.improvements, icon: improveIcon, color: COLORS.danger, fill: COLORS.dangerSoft},
    ].filter(column => column.items.length)
    if (feedbackColumns.length) {
        const columnWidth = (INNER - GAP * (feedbackColumns.length - 1)) / feedbackColumns.length
        context.font = `500 30px ${FONT}`
        const lineSets = feedbackColumns.map(column => wrap(context, column.items.join("  ·  "), columnWidth - 72, 2))
        const columnHeight = 110 + Math.max(...lineSets.map(lines => lines.length)) * 42
        feedbackColumns.forEach((column, index) => {
            const x = MARGIN + index * (columnWidth + GAP)
            panel(context, x, y, columnWidth, columnHeight, column.fill, undefined)
            label(context, column.icon, column.title, column.color, x + 36, y + 32)
            context.fillStyle = COLORS.text
            context.font = `500 30px ${FONT}`
            lineSets[index].forEach((line, lineIndex) => context.fillText(line, x + 36, y + 90 + lineIndex * 42))
        })
        y += columnHeight + GAP
    }

    // Footer: makes clear this is a LinguaScore practice result
    const height = Math.max(MIN_HEIGHT, y + 96)
    context.strokeStyle = COLORS.border
    context.lineWidth = 2
    context.beginPath()
    context.moveTo(MARGIN, height - 92)
    context.lineTo(WIDTH - MARGIN, height - 92)
    context.stroke()
    context.fillStyle = COLORS.faint
    context.font = `500 28px ${FONT}`
    context.textAlign = "center"
    context.fillText(content.labels.footer, WIDTH / 2, height - 60)

    const output = document.createElement("canvas")
    output.width = WIDTH
    output.height = height
    output.getContext("2d").drawImage(canvas, 0, 0)

    return new Promise<Blob>((resolve, reject) =>
        output.toBlob(blob => blob ? resolve(blob) : reject(new Error("Could not create the image")), "image/png"))
}
